<template>
	<div>
		<div class="education--list">
			<div
				v-for="education in educations"
				:key="education.id"
				class="mb-2"
			>
				<h2>
					<CharacteristicLabel :characteristic="education.schoolType"/>
					<span v-if="education.highest"> - Höchste abgeschlossene Ausbildung</span>
				</h2>
				<Infos :education="education"/>
				<div class="actions">
					<a href="#" @click.prevent="edit(education)">
						<Icon icon="pencil"/>
						Bearbeiten
					</a>
					<a href="#" @click.prevent="educationToRemove = education">
						<Icon icon="bin"/>
						Löschen
					</a>
				</div>
			</div>
		</div>
		<div class="d-flex align-items-center mt-4">
			<div v-if="!askFurther">
				<template v-if="!hasFinishedEducation">
					<h3 v-if="hasEducations">Bitte gib noch deine höchste abgeschlossene Ausbildung ein.</h3>
					<SelectInline
						v-if="!editingEducation"
						v-model="schoolType"
						:characteristic-items="characteristicItems"
						initial-show-all
						single-choice
						:disabled="noEducation"
						:description-config="{ inline: true, html: true }"
						@input="onSchoolTypeSelected"
					/>
					<b-form-checkbox v-model="noEducation" class="mt-4" @input="emitChangeEvent">
						Ich habe bisher keine Ausbildung abgeschlossen
					</b-form-checkbox>
				</template>
				<Button
					v-else
					secondary
					smaller
					:disabled="noEducation"
					:text="educations.length === 0 ? 'Ausbildung hinzufügen' : 'Weitere Ausbildung hinzufügen'"
					@click="addEducation"
				/>
			</div>
		</div>
		<div v-if="askFurther">
			<h3>
				Hast Du weitere Ausbildungen, egal ob abgeschlossen oder nicht?<br/>
				<span class="sub">Je mehr du eingibst, desto interessanter wird dein Profil!</span>
			</h3>
			<RadioGroup
				v-model="further"
				:options="furtherOptions"
				stacked
				@input="furtherChanged"
			/>
		</div>
		<ConfirmPopUp
			v-if="educationToRemove"
			title="Ausbildung entfernen"
			@confirm="removeEducation"
			@hidden="educationToRemove = undefined"
		>
			Soll die Ausbildung
			<CharacteristicLabel :characteristic="educationToRemove.schoolType" class="font-weight-extra-bold"/>
			wirklich entfernt werden?
		</ConfirmPopUp>
	</div>
</template>

<script>
import {uuid} from "vue-uuid";
import CharacteristicLabel from "@/vue/characteristic/Label";
import IconLink from "@/vue/common/IconLink";
import Edit from "./education/Edit"
import {mapActions, mapState} from "vuex";
import _ from "lodash";
import Button from "@/vue/common/Button.vue";
import {arrayIsNotEmpty} from "@/helper/Array";
import Icon from "@/vue/icon/Icon.vue";
import ConfirmPopUp from "@/vue/common/popup/ConfirmPopUp.vue";
import RadioGroup from "@/vue/common/form/RadioGroup.vue";
import SelectInline from "@/vue/characteristic/SelectInline.vue";
import Infos from "@/vue/application/education/Infos.vue";
import {COMPLETED} from "@/helper/application/characteristic/EducationCompletion";
import {CAREER_EDUCATION_SCHOOL, hasTag} from "@/helper/characteristic/Tag";
import {isDefined} from "@/helper/Value";
import {JON_INFO__BOT_COLLEGE, JON_INFO__BOT_HIGH_SCHOOL} from "@/vue/application/education/Fan"
import * as NaturalId from "@/helper/characteristic/NaturalId";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";

const FURTHER__YES_NOW = 'yes-now';
const FURTHER__YES_LATER = 'yes-later';
const FURTHER__NO = 'yes-no';

export default {

	components: {
		Infos,
		SelectInline,
		RadioGroup,
		ConfirmPopUp,
		Icon,
		Button,
		CharacteristicLabel,
		IconLink,
		Edit,
	},

	data ()
	{
		return {
			educations: [],
			schoolType: undefined,
			noEducation: false,
			educationToRemove: undefined,
			educationToEdit: undefined,
			askFurther: false,
			further: undefined,
			editingEducation: false,
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		characteristicItems ()
		{
			return _.filter(
				this.meta.schoolType,
				characteristic => hasTag(characteristic, CAREER_EDUCATION_SCHOOL)
			);
		},
		furtherOptions ()
		{
			return [
				{value: FURTHER__YES_NOW, text: 'Eingeben und Chancen erhöhen'},
				{value: FURTHER__YES_LATER, text: 'Später vollständig eingeben'},
				{value: FURTHER__NO, text: 'Ich habe keine weitere Ausbildung'},
			];
		},
		hasEducations ()
		{
			return arrayIsNotEmpty(this.educations);
		},
		dataValid ()
		{
			if (this.noEducation)
			{
				return true;
			}

			return arrayIsNotEmpty(this.educations)
				&& _.includes([FURTHER__YES_LATER, FURTHER__NO], this.further);
		},
		hasFinishedEducation ()
		{
			return _
				.filter(
					this.educations,
					education => education.educationCompletion?.naturalId === COMPLETED
				)
				.length > 0;
		},
	},

	methods: {
		...mapActions('application/education/fan', {
			showFan: 'show',
		}),
		onSchoolTypeSelected (schoolType)
		{
			this.edit({
				id: uuid.v4(),
				schoolType: _.first(schoolType).item,
			});
		},
		furtherChanged (value)
		{
			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(
						_.find(this.furtherOptions, option => option.value === value)?.text
					)
			);

			if (value === FURTHER__YES_NOW)
			{
				this.addEducation();
			}

			this.emitChangeEvent();
		},
		addEducation ()
		{
			this.edit(undefined);
		},
		edit (education)
		{
			this.editingEducation = true;

			const schoolType = education?.schoolType;
			const hadEducations = this.hasEducations;

			let jonInfo = undefined;

			if (schoolType?.naturalId === NaturalId.SCHOOL_TYPE__COLLEGE)
			{
				jonInfo = JON_INFO__BOT_COLLEGE;
			}

			if (schoolType?.naturalId === NaturalId.SCHOOL_TYPE__HIGH_SCHOOL)
			{
				jonInfo = JON_INFO__BOT_HIGH_SCHOOL;
			}

			this.showFan({
				education,
				withSchoolTypeSelection: !isDefined(schoolType),
				withDetails: false,
				jonInfo,
				onClose: ({education}) =>
				{
					this.further = undefined;
					this.schoolType = undefined;

					if (education)
					{
						this.educations = _.filter(this.educations, e => e.id !== education.id);

						this.educations.push(education);

						this.$nextTick(() =>
						{
							if (this.hasFinishedEducation)
							{
								this.askFurther = true;

								if (!hadEducations)
								{
									this.$emit('furtherAppeared');
								}
							}
							else
							{
								this.askFurther = false;
							}
						});

						this.$emit('educationsChanged', {
							educationsCount: this.educations.length
						});

						this.emitChangeEvent();
					}

					this.editingEducation = false;
				}
			});
		},
		removeEducation ()
		{
			this.educations = _.filter(this.educations, e => e.id !== this.educationToRemove.id);

			if (this.educations.length === 0)
			{
				this.further = undefined;
				this.askFurther = false;
			}

			this.educationToRemove = undefined;

			this.$emit('educationsChanged', {
				educationsCount: this.educations.length
			});

			this.emitChangeEvent();
		},
		emitChangeEvent ()
		{
			if (!this.dataValid)
			{
				this.$emit('input', undefined);
				return;
			}

			this.$emit('input', {
				items: !this.noEducation
					? this.educations
					: [],
				skipped: this.further === FURTHER__YES_LATER
			});
		}
	},
}
</script>
